import { useFormat, useTranslation } from '@dreamstack/i18n'
import { SimpleDefinitionList } from '@dreamstack/simple-components'
import type { EnergyCertificate as PropertyEnergyCertificate } from '@dreamstack/web/lib/prisma/properties'
import map from 'lodash/map'
import type { FunctionComponent } from 'react'
import 'twin.macro'
import tw from 'twin.macro'
import { GridWithGapsNoCols, HeadingH3, Text_16_24 } from '../index'

const EnergyClassIndicator = tw.div`absolute bottom-0 h-px-16 w-px-16 -mb-px-8
 md:(h-px-32 w-px-32 -mb-px-16) lg:(h-px-48 w-px-48 -mb-px-24) rounded-full bg-accentroGray-full`

const StyledDefinitionList = tw(
  SimpleDefinitionList
)`mb-px-16 md:mb-px-32 lg:mb-px-40`

const energyClasses = [
  { bgColor: '#7ea4bc', energyClass: 'A++', energyValue: '0' },
  { bgColor: '#508090', energyClass: 'A+', energyValue: '25' },
  { bgColor: '#3e926c', energyClass: 'A', energyValue: '50' },
  { bgColor: '#73c49f', energyClass: 'B', energyValue: '75' },
  { bgColor: '#9dd1ba', energyClass: 'C', energyValue: '100' },
  { bgColor: '#f0d257', energyClass: 'D', energyValue: '125' },
  { bgColor: '#e9b025', energyClass: 'E', energyValue: '150' },
  { bgColor: '#e97425', energyClass: 'F', energyValue: '175' },
  { bgColor: '#e94e25', energyClass: 'G', energyValue: '200+' },
]

export const EnergyCertificate: FunctionComponent<
  React.PropsWithChildren<{
    certificate: Partial<PropertyEnergyCertificate>
  }>
> = ({ certificate }) => {
  const t = useTranslation()
  const formatDate = useFormat()
  const formattedCertificationEndDate = certificate.energy_certificate_end_date
    ? formatDate(certificate.energy_certificate_end_date, 'dd.MM.yyyy')
    : '-'
  const formattedThermalCharacteristics =
    (certificate.thermal_characteristic?.toString() ?? '-') + ' kWh (m2xa)'
  const formattedConstructionYear = certificate.construction_year
    ? certificate.construction_year.toString()
    : '-'
  const propertyEnergyClass = certificate.energy_efficiency_class
  return (
    <GridWithGapsNoCols tw="grid grid-cols-10">
      <div tw="md:col-span-2 lg:col-span-3">
        <StyledDefinitionList
          big
          dt={formattedConstructionYear}
          dd={<Text_16_24>{t('accentro:yearOfConstruction')}</Text_16_24>}
        />
      </div>
      <div tw="col-span-full md:col-span-4 lg:col-span-3">
        <StyledDefinitionList
          big
          dt={certificate.heating_type || '-'}
          dd={<Text_16_24>{t('accentro:heatingType')}</Text_16_24>}
        />
        <StyledDefinitionList
          big
          dt={certificate.firing_types || '-'}
          dd={<Text_16_24>{t('accentro:typeOfFiring')}</Text_16_24>}
        />
        <StyledDefinitionList
          big
          dt={t(
            `accentro:yes-no-boolean-${
              certificate.energy_consumption_contains_warm_water ?? false
            }`
          )}
          dd={<Text_16_24>{t('accentro:energyWithHotWater')}</Text_16_24>}
        />
      </div>
      <div tw="col-span-full md:col-span-4 lg:col-span-3">
        <StyledDefinitionList
          big
          dt={certificate.building_energy_rating_type || '-'}
          dd={<Text_16_24>{t('accentro:energyRatingType')}</Text_16_24>}
        />
        <StyledDefinitionList
          big
          dt={formattedThermalCharacteristics}
          dd={<Text_16_24>{t('accentro:thermalCharacteristics')}</Text_16_24>}
        />
        <StyledDefinitionList
          big
          dt={formattedCertificationEndDate}
          dd={<Text_16_24>{t('accentro:certificateValidTill')}</Text_16_24>}
        />
      </div>
      <div tw="col-span-full lg:col-span-9 flex items-stretch mt-px-24 mb-px-64 md:mb-px-96">
        {map(energyClasses, (e, idx) => {
          const showIndicator = propertyEnergyClass === e.energyClass
          return (
            <div
              key={idx}
              tw="flex items-center justify-center flex-1 py-px-12 md:py-px-24 relative h-px-40 md:h-auto"
              style={{ backgroundColor: e.bgColor }}
            >
              <HeadingH3 tw="text-accentroWhite-full text-px-13 md:text-px-28 lg:text-px-32">
                {e.energyClass}
              </HeadingH3>
              {showIndicator && <EnergyClassIndicator />}
              <div tw="absolute right-0 -bottom-full md:-bottom-1/2">
                <div tw="mt-px-24 md:mt-0 relative text-px-11 leading-px-18  md:(left-1/2 text-px-13 leading-px-18) lg:(text-px-16 leading-px-24)">
                  {e.energyValue}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </GridWithGapsNoCols>
  )
}
